import _ from "lodash";
import { parse } from "tldts";

import type { MenstrualCycleDiagramProps } from "../components/MenstrualCycleDiagram";
import { PRODUCTION, SUBDOMAIN_OVERRIDE } from "../constants";
import { createOnboardingState } from "../screens/New_DIY_form/createOnboardingState";
import type {
  DietEnum as DietaryPreference,
  DjstripeCustomer,
  DjstripeSubscription,
  DjstripeSubscriptionStatusEnum,
  ExerciseTemplate,
  Organisation,
  User,
  UserDislikeRecipe,
  UserFavouriteRecipe,
} from "../services/backendTypes";
import type { OnboardingState } from "../slices/onboardingSlice";
import type {
  UserDislikeRecipeByRecipeTemplateId,
  UserFavouriteRecipeByRecipeTemplateId,
  UserSleepsByDate,
  UserStressByDate,
  UserWeightsByDate,
} from "../slices/userSlice";
import { DietaryPreferenceEnum } from "../types";
import { FeatureFlag as FeatureFlagProper, isFeatureFlagActive } from "./featureFlags";

// eslint-disable-next-line import/prefer-default-export
export function isUserLoggedIn(user: User | null): boolean {
  return Boolean(user);
}

export function getFavouritedRecipe(
  userFavouriteRecipeByRecipeTemplateId: UserFavouriteRecipeByRecipeTemplateId,
  recipeTemplateId: number
): UserFavouriteRecipe | undefined {
  return userFavouriteRecipeByRecipeTemplateId[recipeTemplateId];
}

export function getDislikedRecipe(
  userDislikeRecipeByRecipeTemplateId: UserDislikeRecipeByRecipeTemplateId,
  recipeTemplateId: number
): UserDislikeRecipe | undefined {
  return userDislikeRecipeByRecipeTemplateId[recipeTemplateId];
}

export const getLatestMeasurementForDate = (
  measurements: UserWeightsByDate | UserSleepsByDate | UserStressByDate,
  date: string
): number | undefined => {
  const measurementOnDate = measurements[date];

  if (!measurementOnDate) return undefined;

  const orderedMeasurements = _.orderBy(measurementOnDate, "created_at", "desc");

  const measurement = _.head(orderedMeasurements);

  if (!measurement) {
    return undefined;
  }

  if (typeof measurement === "number") {
    return measurement;
  }

  // The compiler is acting strangely here and we override it
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const measurementValue = measurement.value;

  switch (measurementValue) {
    case "VERY_LOW":
      return 1;
    case "LOW":
      return 2;
    case "MEDIUM":
      return 3;
    case "HIGH":
      return 4;
    case "VERY_HIGH":
      return 5;
    default:
      return measurementValue;
  }
};

export function getOrganisation(user: User): Organisation | undefined {
  return user?.coach?.organisation || user?.organisation;
}

export function getKcalPerSession(
  exerciseTemplateIdRaw: number | string,
  minutesPerSession: number,
  weight: number,
  exerciseTemplates?: ExerciseTemplate[]
): number {
  const exerciseTemplateId = Number(exerciseTemplateIdRaw);
  const exerciseTemplate = _.find(exerciseTemplates, { id: exerciseTemplateId });

  if (!exerciseTemplate || typeof exerciseTemplate === "number") return 0;

  const kcalPerMin: number = exerciseTemplate.kcal_per_kg_per_min;

  return kcalPerMin * weight * minutesPerSession;
}

export const getUserSubscriptionStatus = (user: User): DjstripeSubscriptionStatusEnum[] | undefined => {
  let customer: DjstripeCustomer | undefined;

  if (user?.is_coach) {
    customer = _.first(user.customer);
  } else {
    return undefined;
  }

  const statuses = _.map(customer?.subscriptions, "status");

  if (_.isEmpty(statuses)) {
    return undefined;
  }

  return statuses;
};

const INVALID_SUBSCRIPTION_STATUSES: DjstripeSubscriptionStatusEnum[] = [
  "canceled",
  "unpaid",
  "incomplete_expired",
  // Note: The status "paused" is still beta (but we use it in production) and not supported by djstripe
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  "paused",
];

export const userAccountIsDisabledOrHasInValidSubscription = (user: User | null): boolean => {
  if (!user) {
    return false;
  }

  if (user.payment_status_override === true) {
    // Always allow the user to login if payment_status_override is set to true
    return false;
  }

  if (!user.account_enabled || (user.coach && !user.coach?.account_enabled)) {
    return true;
  }

  const statuses = getUserSubscriptionStatus(user);

  if (!statuses || _.isEmpty(statuses)) {
    return false;
  }

  const isStatusInvalid = (status: DjstripeSubscriptionStatusEnum): boolean =>
    INVALID_SUBSCRIPTION_STATUSES.includes(status);

  const validStatuses = _.reject(statuses, isStatusInvalid);

  return _.isEmpty(validStatuses);
};

// Stripe subscriptions

/**
 * If customer is trialing (on any subscription)
 */
export function doesCustomerHaveAnyTrialingSubscriptions(user: User): boolean {
  return _.some(user?.customer?.[0]?.subscriptions, { status: "trialing" });
}

const SELF_SERVICE_PRODUCT_STRIPE_ID = "prod_MlNaQY9hwHvdKS";
const SELF_SERVICE_PRODUCT_STRIPE_IDS = [
  // Live mode
  SELF_SERVICE_PRODUCT_STRIPE_ID,
  // Test mode
  "prod_Ml1OptlpTI1FH7",
];
const LEGACY_CUSTOM_BRANDING_PRODUCT_STRIPE_ID = "prod_MlNaQY9hwHvdKS";
const LEGACY_CUSTOM_BRANDING_PRODUCT_STRIPE_IDS = [
  // Live mode
  LEGACY_CUSTOM_BRANDING_PRODUCT_STRIPE_ID,
  // Test mode
  "prod_LYTjPIADGXfoDd",
];

function isCustomerOnOurSelfServicePlan(user: User): boolean {
  // eslint-disable-next-line no-restricted-syntax
  for (const subscription of user?.customer?.[0]?.subscriptions || []) {
    // eslint-disable-next-line no-restricted-syntax
    for (const item of subscription.items) {
      if (item.plan.product && SELF_SERVICE_PRODUCT_STRIPE_IDS.includes(item.plan.product)) {
        return true;
      }
    }
  }

  return false;
}

function doesCustomerHaveValidSubscription(user: User, product: string): boolean {
  // NOTE: The compiler does not work well with lodash filter and typescript
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  // const matchingSubscriptions: DjstripeSubscription[] = _.filter(user?.customer?.[0]?.subscriptions, {
  //   plan: { product },
  // });
  const matchingSubscriptions: DjstripeSubscription[] = _.filter(
    user?.customer?.[0]?.subscriptions,
    // (subscription) => subscription?.plan?.product === product
    (subscription) => {
      _.some(subscription?.items, (item) => item.plan.product === product);
    }
  );

  const isAnySubscriptionAValidSubscription = _.some(
    matchingSubscriptions,
    (subscription) => !_.includes(INVALID_SUBSCRIPTION_STATUSES, subscription.status)
  );

  return isAnySubscriptionAValidSubscription;
}

/**
 * Note that this fails open to ensure no-one is accidentally blocked.
 * @param user
 * @returns
 */
export function shouldB2CCustomerHavePaidAccess(user: User | null): boolean {
  if (!user) {
    return true;
  }

  if (user.payment_status_override === true) {
    return true;
  }

  if (!isFeatureFlagActive(user, FeatureFlagProper.FF_B2C_PAYWALL)) {
    return true;
  }

  if (!user.coach) {
    return true;
  }

  if (!user.coach.organisation) {
    return true;
  }

  if (!user.coach.organisation.product_stripe_id) {
    return true;
  }

  if (!user.customer) {
    return true;
  }

  const hasNoSubscriptions = !user.customer?.[0]?.subscriptions || user.customer[0].subscriptions.length === 0;
  if (hasNoSubscriptions) {
    return true;
  }

  const isAnySubscriptionAValidSubscription = _.some(
    user?.customer?.[0]?.subscriptions,
    (subscription) => !_.includes(INVALID_SUBSCRIPTION_STATUSES, subscription.status)
  );

  return isAnySubscriptionAValidSubscription;
}

export function shouldCustomerHaveAccessToAccountManagementFunctionality(user: User): boolean {
  return (
    // doesCustomerHaveValidSubscription(user, LEGACY_CUSTOM_BRANDING_PRODUCT_STRIPE_ID) ||
    _.some(LEGACY_CUSTOM_BRANDING_PRODUCT_STRIPE_IDS, (productId) =>
      doesCustomerHaveValidSubscription(user, productId)
    ) || isCustomerOnOurSelfServicePlan(user)
  );
}

export function shouldShowCustomBrandingHeader(user: User): boolean {
  return Boolean(user?.coach.organisation && user?.coach.organisation?.logo_visible);
}

export function isUserDiy(user: User): boolean {
  return Boolean(getOrganisation(user)?.clients_are_diy);
}

export function isUserFromSimpleOrganization(user: User): boolean {
  const organisation = getOrganisation(user);
  return Boolean(organisation?.recipe_system_type === "SIMPLE");
}

export function hasUserCompletedTheirDiyNutritionPlanningFormCompletely(user: User): boolean {
  return Boolean(user?.intake && user?.intake.weekly_nutrition_plan);
}

export const getHostname = (): string => window.location.hostname;

/**
 * We host customer portals on subdomains of our main domain.
 * This function checks if we are on a subdomain of our main domain and
 * therefore should do the logic for a customer portal
 *
 * @param hostname ie, "trainmore.weekmeals.co"
 * @returns
 */
export function getManagedCustomerPortalSubdomain(hostname: string): string {
  if (SUBDOMAIN_OVERRIDE && !PRODUCTION) {
    // This is for development purposes
    return SUBDOMAIN_OVERRIDE;
  }

  const parsedDomain = parse(hostname);

  const { subdomain } = parsedDomain;

  // Ignore PR branch subdomains
  if (subdomain && subdomain.startsWith("pr-") && hostname.includes("amplifyapp.com")) {
    return "";
  }

  if (subdomain === "www" || subdomain === "app") {
    return "";
  }

  return subdomain || "";
}

/**
 * DEPRECATED: We now use groups to manage feature flags. This is here for legacy compatibility.
 * @param organisation
 * @param flag
 * @returns
 */
export enum FeatureFlag {
  // eslint-disable-next-line no-unused-vars
  DisableProductLogging = "feature_flag_disable_product_logging",
}

/**
 * DEPRECATED: We now use groups to manage feature flags. This is here for legacy compatibility.
 * @param organisation
 * @param flag
 * @returns
 */
export function isFeatureFlagEnabled(organisation: Organisation | null | undefined, flag: FeatureFlag): boolean {
  if (!organisation) return false;
  return _.get(organisation, flag, false);
}

export function doesOrganisationHaveManagedPlanning(organisation: Organisation): boolean {
  return Boolean(organisation?.clients_are_diy);
}

export function isUserInPaymentRequiredOrganisation(user: User): boolean {
  if (user.payment_status_override === true) {
    return false;
  }

  const organisation = getOrganisation(user);

  // FIXME: We are hardcoding this for now
  return organisation?.subdomain?.toLowerCase() === "killerbody";
  // return Boolean(organisation?.payment_required);
}

/**
 * DEPRECATED: We now use groups to manage feature flags. This is here for legacy compatibility.
 * @param organisation
 * @returns
 */
export function shouldHaveAccessToGenerateMeals(organisation: Organisation | null | undefined): boolean {
  if (!organisation) return false;
  return organisation.feature_flag_generate_meals_button_in_planner || false;
}

/**
 * DEPRECATED: We now use groups to manage feature flags. This is here for legacy compatibility.
 * @param user
 * @returns
 */
export function shouldHaveAccessToCreateYourOwnRecipes(user: User): boolean {
  const organisation = getOrganisation(user);

  if (!organisation) return false;

  return organisation.feature_flag_create_your_own_recipes || false;
}

export function organisationIsDiyBillingForUsers(user: User): boolean {
  // If the organisation has a product set then we are billing for users
  return Boolean(user?.coach?.organisation?.product_stripe_id);
}

/**
 * DEPRECATED: We now use groups to manage feature flags. This is here for legacy compatibility.
 * @param organisation
 * @returns
 */
export function areWeAnOpinionatedRecipeInspirationApp(organisation: Organisation | null): boolean {
  return isFeatureFlagEnabled(organisation, FeatureFlag.DisableProductLogging);
}

const HIGH_PROTEIN_ORGS = ["trainmore", "clubsportive", "pimklement", "happyhealthy", "bamifit", "alexarciab"];

export function isHighProteinOrganisation(): boolean {
  const subdomain = getManagedCustomerPortalSubdomain(getHostname());

  // NOTE: app and www are ""
  if (!subdomain) return true;

  return HIGH_PROTEIN_ORGS.includes(subdomain.toLowerCase());
}

export function isLowProteinOrganisation(): boolean {
  return !isHighProteinOrganisation();
}

// TODO: Note that this is temporary to allow testing in production with an app that is not yet released.
// Once testing is complete, we should remove this function.
export function isImperialUnitsAllowedOrganisation(): boolean {
  const subdomain = getManagedCustomerPortalSubdomain(getHostname());

  return subdomain.toLowerCase() === "alexarciab";
}

export function isKillerbodySubdomain(): boolean {
  const subdomain = getManagedCustomerPortalSubdomain(getHostname());

  return subdomain.toLowerCase() === "killerbody";
}

export function isBamifitSubdomain(): boolean {
  const subdomain = getManagedCustomerPortalSubdomain(getHostname());

  return subdomain.toLowerCase() === "bamifit";
}

export function isFuelAndFlowSubdomain(): boolean {
  const subdomain = getManagedCustomerPortalSubdomain(getHostname());

  return subdomain.toLowerCase() === "alexarciab";
}

export function isFluffyToFitSubdomain(): boolean {
  const subdomain = getManagedCustomerPortalSubdomain(getHostname());

  return subdomain.toLowerCase() === "fluffytofit";
}

export function isNateBowerSubdomain(): boolean {
  const subdomain = getManagedCustomerPortalSubdomain(getHostname());

  return subdomain.toLowerCase() === "natebower";
}

export function isDmaSubdomain(): boolean {
  const subdomain = getManagedCustomerPortalSubdomain(getHostname());

  return subdomain.toLowerCase() === "dma";
}

/**
 * Calculates the current day of the menstrual cycle for a user
 * @param onboardingState The onboarding state object
 * @param date Optional date to calculate for (defaults to today)
 * @returns Number representing the current day of the cycle (1-based), or null if not applicable/not enough data
 */
export function getCurrentMenstrualCycleDay(onboardingState: OnboardingState, date?: Date): number | null {
  if (!onboardingState.menstrualCycle) return null;

  const {
    biometricData: { gender },
    menstrualCycle: { lastMenstrualPeriodStartDate, menstrualCycleLength },
  } = onboardingState;

  // Return null for users where menstrual cycle is not relevant
  if (gender !== "FEMALE") return null;

  // Return null if we don't have the necessary data to calculate
  if (!lastMenstrualPeriodStartDate || !menstrualCycleLength) return null;

  const lastPeriodDate = new Date(lastMenstrualPeriodStartDate);
  const targetDate = date || new Date();

  // Calculate days since last period
  const daysSinceLastPeriod = Math.floor((targetDate.getTime() - lastPeriodDate.getTime()) / (1000 * 60 * 60 * 24));

  // Calculate current day in cycle (1-based)
  const currentDayInCycle = (daysSinceLastPeriod % menstrualCycleLength) + 1;

  return currentDayInCycle;
}

/**
 * Calculates the date of the next menstruation for a user
 * @param onboardingState The onboarding state object
 * @param date Optional date to calculate from (defaults to today)
 * @returns Date object representing the next menstruation start date, or null if not applicable/not enough data
 */
export function dateOfNextMenstruation(onboardingState: OnboardingState, date?: Date): Date | null {
  if (!onboardingState.menstrualCycle) return null;

  const {
    biometricData: { gender },
    menstrualCycle: { lastMenstrualPeriodStartDate, menstrualCycleLength },
  } = onboardingState;

  // Return null for users where menstrual cycle is not relevant
  if (gender !== "FEMALE") return null;

  // Return null if we don't have the necessary data to calculate
  if (!lastMenstrualPeriodStartDate || !menstrualCycleLength) return null;

  const lastPeriodDate = new Date(lastMenstrualPeriodStartDate);
  const targetDate = date || new Date();

  // Calculate days since last period
  const daysSinceLastPeriod = Math.floor((targetDate.getTime() - lastPeriodDate.getTime()) / (1000 * 60 * 60 * 24));

  // Calculate how many complete cycles have passed
  const completeCycles = Math.floor(daysSinceLastPeriod / menstrualCycleLength);

  // Calculate the next cycle start date
  const nextMenstruationDate = new Date(lastPeriodDate);
  nextMenstruationDate.setDate(lastPeriodDate.getDate() + (completeCycles + 1) * menstrualCycleLength);

  // If the next date is in the past (which shouldn't happen but just to be safe),
  // add another cycle
  if (nextMenstruationDate <= targetDate) {
    nextMenstruationDate.setDate(nextMenstruationDate.getDate() + menstrualCycleLength);
  }

  return nextMenstruationDate;
}

/**
 * Calculates the number of days of menstruation based on onboarding data
 * @param onboardingState The onboarding state object
 * @returns Number of menstruation days, or default value (5) if not enough data
 */
export function getNumberOfDaysOfMenstruation(onboardingState: OnboardingState): number {
  // We no longer track the last day of menstruation so we just return 5 here
  return 5;
}

/**
 * Gets menstrual cycle diagram props from onboarding state
 * @param onboardingState The onboarding state object
 * @param date Optional date to calculate for (defaults to today)
 * @returns Props for rendering a menstrual cycle diagram
 */
export function getMenstrualCycleProps(onboardingState: OnboardingState, date?: Date): MenstrualCycleDiagramProps {
  const nextPeriodDate = dateOfNextMenstruation(onboardingState, date);
  const targetDate = date || new Date();

  // Calculate days until next period if we have a valid date
  const daysUntilNextPeriod = nextPeriodDate
    ? Math.ceil((nextPeriodDate.getTime() - targetDate.getTime()) / (1000 * 60 * 60 * 24))
    : 28;

  return {
    numberOfDaysInCycle: onboardingState.menstrualCycle?.menstrualCycleLength || 28,
    numberOfMenstruationDays: getNumberOfDaysOfMenstruation(onboardingState),
    currentDayInCycle: getCurrentMenstrualCycleDay(onboardingState, date) || 1,
    daysUntilNextPeriod,
  };
}

/**
 * Gets menstrual cycle diagram props from a user object
 * @param user The user object
 * @param date Optional date to calculate for (defaults to today)
 * @returns Props for rendering a menstrual cycle diagram
 */
export function getMenstrualCyclePropsFromUser(user: User, date?: Date): MenstrualCycleDiagramProps | undefined {
  if (!user.intake) {
    return undefined;
  }

  const onboardingState = createOnboardingState(user.intake);

  return getMenstrualCycleProps(onboardingState, date);
}

/**
 * Enum representing different phases of the menstrual cycle
 * FIXME: Once the backend is updated we should use the generated types
 */
export enum MenstrualCyclePhase {
  Menstruation = "Menstruation",
  Follicular = "Follicular",
  Ovulation = "Ovulation",
  Luteal = "Luteal",
}

/**
 * Determines which phase of the menstrual cycle a user is currently in
 * @param onboardingState The onboarding state object
 * @param date Optional date to calculate for (defaults to today)
 * @returns The current menstrual cycle phase, or undefined if not applicable
 */
export function getCurrentMenstrualCyclePhase(
  onboardingState: OnboardingState,
  date?: Date
): MenstrualCyclePhase | undefined {
  if (!onboardingState.menstrualCycle) return undefined;

  const {
    biometricData: { gender },
    menstrualCycle: { lastMenstrualPeriodStartDate, menstrualCycleLength },
  } = onboardingState;

  // Return undefined for users where menstrual cycle is not relevant
  if (gender !== "FEMALE") return undefined;

  // Return undefined if we don't have the necessary data to calculate
  if (!lastMenstrualPeriodStartDate || !menstrualCycleLength) return undefined;

  const currentDay = getCurrentMenstrualCycleDay(onboardingState, date);
  if (currentDay === null) return undefined;

  const menstruationDays = getNumberOfDaysOfMenstruation(onboardingState);

  // Calculate phase boundaries
  // Typically, ovulation occurs around day 14 in a 28-day cycle, so we adjust proportionally
  const ovulationDay = Math.round(menstrualCycleLength / 2);
  const ovulationPhaseStart = ovulationDay - 1;
  const ovulationPhaseEnd = ovulationDay + 1;

  // Determine cycle phase
  if (currentDay <= menstruationDays) {
    return MenstrualCyclePhase.Menstruation;
  }
  if (currentDay < ovulationPhaseStart) {
    return MenstrualCyclePhase.Follicular;
  }
  if (currentDay >= ovulationPhaseStart && currentDay <= ovulationPhaseEnd) {
    return MenstrualCyclePhase.Ovulation;
  }
  return MenstrualCyclePhase.Luteal;
}

/**
 * Determines which phase of the menstrual cycle a user is currently in
 * @param user The user object
 * @param date Optional date to calculate for (defaults to today)
 * @returns The current menstrual cycle phase, or undefined if not applicable
 */
export function getCurrentMenstrualCyclePhaseFromUser(user: User, date?: Date): MenstrualCyclePhase | undefined {
  if (!user.intake) {
    return undefined;
  }

  const onboardingState = createOnboardingState(user.intake);
  return getCurrentMenstrualCyclePhase(onboardingState, date);
}

export function doesTheUserHaveMenstrualPlanSet(user: User): boolean {
  return Boolean(user.intake?.last_menstrual_period_start_date);
}

/**
 * Determines if the user has a plant-based diet (vegan or vegetarian)
 * @param dietaryPreference The user's dietary preference
 * @returns True if the user has a vegan or vegetarian diet
 */
export function isPlantBasedDiet(dietaryPreference: DietaryPreference | undefined): boolean {
  return dietaryPreference === DietaryPreferenceEnum.VEGAN || dietaryPreference === DietaryPreferenceEnum.VEGETARIAN;
}

/**
 * Determines if the hormone indicator should be shown for a recipe based on tags and feature flags.
 * @param user The user object
 * @param recipeTags Array of tags from the recipe template
 * @returns True if the hormone indicator should be shown
 */
export function shouldShowHormoneIndicator(user: User | null, recipeTags: { name: string }[] | undefined): boolean {
  if (!recipeTags) {
    return false;
  }
  const hasHormoneTag = recipeTags.some((tag) => tag.name === "HORMONE");
  if (!hasHormoneTag) {
    return false;
  }

  const isFemaleHormonesEnabled = isFeatureFlagActive(user, FeatureFlagProper.FF_FEMALE_HORMONES);
  const isMenopauseOnlyEnabled = isFeatureFlagActive(user, FeatureFlagProper.FF_FEMALE_HORMONES_MENOPAUSE_ONLY);

  return isFemaleHormonesEnabled || isMenopauseOnlyEnabled;
}

/**
 * Determines if a user belongs to a simple organization (recipe_system_type === "SIMPLE").
 * @param user The user object
 * @returns True if the user's organization uses the simple recipe system
 */
export function isSimpleOrg(user: User): boolean {
  const organisation = getOrganisation(user);
  return organisation?.recipe_system_type === "SIMPLE";
}

/**
 * Determines if the custom Intercom launcher should be shown for the user.
 * The custom launcher is hidden for:
 * - happyhealthy subdomain (they handle their own customer service)
 * - Legacy clients (COMPLEX recipe system type)
 *
 * @param user - The current user
 * @returns true if the custom launcher should be shown, false otherwise
 */
export function shouldShowCustomIntercomLauncher(user: User | null): boolean {
  // Hide for happyhealthy subdomain
  if (getManagedCustomerPortalSubdomain(getHostname()) === "happyhealthy") {
    return false;
  }

  // If no user, don't show
  if (!user) {
    return false;
  }

  // Show for coach users
  if (user.is_coach) {
    return true;
  }

  // Show for DIY users
  if (isUserDiy(user)) {
    return true;
  }

  // Show for Simple organization users
  if (isUserFromSimpleOrganization(user)) {
    return true;
  }

  // Hide for legacy clients (COMPLEX recipe system or neither DIY nor Simple)
  return false;
}

/**
 * Determines whether the Progress tab should be shown for a given user.
 * SIMPLE orgs should use Recipe Inspiration instead of Progress tracking.
 * @param user The user object (can be null)
 * @returns False if the Progress tab should be shown, True if it should be hidden
 */
export function shouldShowProgressTab(user: User | null): boolean {
  if (!user) {
    return false; // Show tab if user is not available
  }

  // Hide Progress tab for SIMPLE orgs (they should use Recipe Inspiration instead)
  if (isSimpleOrg(user)) {
    return false; // Hide the tab
  }

  return true; // Show the tab for other organization types
}

/**
 * Determines if a simple organization user has completed their onboarding.
 * Simple onboarding requires: motivation, cooking styles, and preferred meal times.
 * @param user The user object
 * @returns True if the user has completed simple onboarding
 */
export function hasUserCompletedSimpleOnboarding(user: User): boolean {
  // Check if organization is using simple recipe system
  if (!isSimpleOrg(user)) {
    return true; // Not a simple organization, so consider as "completed"
  }

  // Check if all required simple onboarding fields are populated
  const hasMotivation = user.intake?.onboarding_motivation !== null && user.intake?.onboarding_motivation !== undefined;
  const hasCookingStyles =
    user.intake?.onboarding_cooking_styles !== null &&
    user.intake?.onboarding_cooking_styles !== undefined &&
    user.intake?.onboarding_cooking_styles.length > 0;
  const hasPreferredMealTimes =
    user.intake?.preferred_meal_times !== null &&
    user.intake?.preferred_meal_times !== undefined &&
    user.intake?.preferred_meal_times.length > 0;

  return hasMotivation && hasCookingStyles && hasPreferredMealTimes;
}
